<template>
  <div class="calculator-container" :dir="currentLanguage === 'ar' ? 'rtl' : 'ltr'">
    <section class="section" id="calculator">
      <div class="container tool-shell">
        <div class="calculator-card">
          <div class="step-header">
            <div class="step-title">
              <h2>{{ currentStepData.title }}</h2>
              <p>{{ currentStepData.desc }}</p>
            </div>
            <div class="progress-wrap">
              <div class="progress-label">{{ t.step }} {{ currentStep }} {{ t.of }} {{ totalSteps }}</div>
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
              </div>
            </div>
          </div>

          <div class="step-body">
            <form @submit.prevent novalidate>
              
              <!-- Step 1 -->
              <div class="step" :class="{ active: currentStep === 1 }" v-show="currentStep === 1">
                <div class="form-grid">
                  <div class="field full">
                    <label>{{ t.serviceCategoryLabel }}</label>
                    <div class="industry-grid">
                      <label class="option-card" :class="{ selected: formData.serviceCategory === 'medical' }"><input type="radio" v-model="formData.serviceCategory" value="medical"> {{ t.catMedical }}</label>
                      <label class="option-card" :class="{ selected: formData.serviceCategory === 'education' }"><input type="radio" v-model="formData.serviceCategory" value="education"> {{ t.catEducation }}</label>
                      <label class="option-card" :class="{ selected: formData.serviceCategory === 'professional' }"><input type="radio" v-model="formData.serviceCategory" value="professional"> {{ t.catProfessional }}</label>
                      <label class="option-card" :class="{ selected: formData.serviceCategory === 'field' }"><input type="radio" v-model="formData.serviceCategory" value="field"> {{ t.catField }}</label>
                      <label class="option-card" :class="{ selected: formData.serviceCategory === 'rental' }"><input type="radio" v-model="formData.serviceCategory" value="rental"> {{ t.catRental }}</label>
                      <label class="option-card" :class="{ selected: formData.serviceCategory === 'lifestyle' }"><input type="radio" v-model="formData.serviceCategory" value="lifestyle"> {{ t.catLifestyle }}</label>
                    </div>
                    <span class="helper">{{ t.serviceCategoryHelper }}</span>
                  </div>

                  <div class="field">
                    <label for="serviceType">{{ t.serviceTypeLabel }}</label>
                    <select id="serviceType" v-model="formData.serviceType">
                      <option v-for="option in currentServiceOptions" :key="option.value" :value="option.value">
                        {{ currentLanguage === 'en' ? option.labelEn : option.label }}
                      </option>
                    </select>
                    <span class="helper">{{ t.serviceTypeHelper }}</span>
                  </div>

                  <div class="field">
                    <label for="currency">{{ t.currencyLabel }}</label>
                    <select id="currency" v-model="formData.currency">
                      <option value="ج.م">{{ currentLanguage === 'en' ? 'EGP - Egyptian Pound' : 'جنيه مصري EGP' }}</option>
                      <option value="ر.س">{{ currentLanguage === 'en' ? 'SAR - Saudi Riyal' : 'ريال سعودي SAR' }}</option>
                      <option value="د.إ">{{ currentLanguage === 'en' ? 'AED - UAE Dirham' : 'درهم إماراتي AED' }}</option>
                      <option value="$">{{ currentLanguage === 'en' ? 'USD - US Dollar' : 'دولار أمريكي USD' }}</option>
                      <option value="د.ك">{{ currentLanguage === 'en' ? 'KWD - Kuwaiti Dinar' : 'دينار كويتي KWD' }}</option>
                      <option value="ر.ق">{{ currentLanguage === 'en' ? 'QAR - Qatari Riyal' : 'ريال قطري QAR' }}</option>
                      <option value="ر.ع">{{ currentLanguage === 'en' ? 'OMR - Omani Rial' : 'ريال عماني OMR' }}</option>
                      <option value="د.ب">{{ currentLanguage === 'en' ? 'BHD - Bahraini Dinar' : 'دينار بحريني BHD' }}</option>
                    </select>
                  </div>

                  <div class="field full">
                    <label>{{ t.deliveryModeLabel }}</label>
                    <div class="radio-grid">
                      <label class="option-card" :class="{ selected: formData.deliveryMode.includes('center') }"><input type="checkbox" v-model="formData.deliveryMode" value="center"> {{ t.modeCenter }}</label>
                      <label class="option-card" :class="{ selected: formData.deliveryMode.includes('online') }"><input type="checkbox" v-model="formData.deliveryMode" value="online"> {{ t.modeOnline }}</label>
                      <label class="option-card" :class="{ selected: formData.deliveryMode.includes('client_location') }"><input type="checkbox" v-model="formData.deliveryMode" value="client_location"> {{ t.modeClient }}</label>
                    </div>
                    <span class="helper">{{ t.deliveryModeHelper }}</span>
                  </div>

                  <div class="field full">
                    <label for="complexity">{{ t.complexityLabel }}</label>
                    <select id="complexity" v-model="formData.complexity">
                      <option value="none">{{ t.compNone }}</option>
                      <option value="providers">{{ t.compProviders }}</option>
                      <option value="branches">{{ t.compBranches }}</option>
                      <option value="both">{{ t.compBoth }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Step 2 -->
              <div class="step" :class="{ active: currentStep === 2 }" v-show="currentStep === 2">
                <div class="form-grid">
                  <div class="field">
                    <label for="dailyBookings">{{ t.dailyBookingsLabel }}</label>
                    <input type="number" id="dailyBookings" min="0" v-model.number="formData.dailyBookings" required>
                    <span class="helper">{{ t.dailyBookingsHelper }}</span>
                  </div>

                  <div class="field">
                    <label for="avgValue">{{ t.avgValueLabel }}</label>
                    <div class="input-unit">
                      <input type="number" id="avgValue" min="0" v-model.number="formData.avgValue" required>
                      <span class="unit">{{ formData.currency }}</span>
                    </div>
                    <span class="helper">{{ t.avgValueHelper }}</span>
                  </div>

                  <div class="field">
                    <label for="workingDays">{{ t.workingDaysLabel }}</label>
                    <input type="number" id="workingDays" min="1" max="31" v-model.number="formData.workingDays" required>
                    <span class="helper">{{ t.workingDaysHelper }}</span>
                  </div>

                  <div class="field">
                    <label for="avgDuration">{{ t.avgDurationLabel }}</label>
                    <div class="input-unit">
                      <input type="number" id="avgDuration" min="5" v-model.number="formData.avgDuration" required>
                      <span class="unit">{{ t.minutes }}</span>
                    </div>
                    <span class="helper">{{ t.avgDurationHelper }}</span>
                  </div>

                  <div class="field">
                    <label for="resourceCount">{{ t.resourceCountLabel }}</label>
                    <input type="number" id="resourceCount" min="1" v-model.number="formData.resourceCount" required>
                    <span class="helper">{{ t.resourceCountHelper }}</span>
                  </div>

                  <div class="field">
                    <label for="workingHours">{{ t.workingHoursLabel }}</label>
                    <input type="number" id="workingHours" min="1" max="24" v-model.number="formData.workingHours" required>
                    <span class="helper">{{ t.workingHoursHelper }}</span>
                  </div>

                  <div class="field mobile-only">
                    <div class="summary-item">
                      <small>{{ t.expectedRevenue }}</small>
                      <strong>{{ formatMoney(calculatedResults.expectedMonthlyRevenue) }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 3 -->
              <div class="step" :class="{ active: currentStep === 3 }" v-show="currentStep === 3">
                <div class="form-grid">
                  <div class="field full slider-field">
                    <div class="slider-head">
                      <label for="noShowRate">{{ t.noShowRateLabel }}</label>
                      <span class="slider-value"><span>{{ formData.noShowRate }}</span>%</span>
                    </div>
                    <input type="range" id="noShowRate" min="0" max="100" v-model.number="formData.noShowRate">
                  </div>

                  <div class="field full slider-field">
                    <div class="slider-head">
                      <label for="cancelRate">{{ t.cancelRateLabel }}</label>
                      <span class="slider-value"><span>{{ formData.cancelRate }}</span>%</span>
                    </div>
                    <input type="range" id="cancelRate" min="0" max="100" v-model.number="formData.cancelRate">
                  </div>

                  <div class="field full slider-field">
                    <div class="slider-head">
                      <label for="compensatedCancelRate">{{ t.compensatedCancelRateLabel }}</label>
                      <span class="slider-value"><span>{{ formData.compensatedCancelRate }}</span>%</span>
                    </div>
                    <input type="range" id="compensatedCancelRate" min="0" max="100" v-model.number="formData.compensatedCancelRate">
                  </div>

                  <div class="field">
                    <label for="missedCalls">{{ t.missedCallsLabel }}</label>
                    <input type="number" id="missedCalls" min="0" v-model.number="formData.missedCalls" required>
                  </div>

                  <div class="field slider-field">
                    <div class="slider-head">
                      <label for="callConversionRate">{{ t.callConversionRateLabel }}</label>
                      <span class="slider-value"><span>{{ formData.callConversionRate }}</span>%</span>
                    </div>
                    <input type="range" id="callConversionRate" min="0" max="100" v-model.number="formData.callConversionRate">
                  </div>
                </div>
              </div>

              <!-- Step 4 -->
              <div class="step" :class="{ active: currentStep === 4 }" v-show="currentStep === 4">
                <div class="info-box">{{ t.step4Info }}</div>
                <div class="form-grid">
                  <div class="field">
                    <label for="peakLostBookings">{{ t.peakLostBookingsLabel }}</label>
                    <input type="number" id="peakLostBookings" min="0" v-model.number="formData.peakLostBookings" required>
                  </div>

                  <div class="field slider-field">
                    <div class="slider-head">
                      <label for="redistributionRate">{{ t.redistributionRateLabel }}</label>
                      <span class="slider-value"><span>{{ formData.redistributionRate }}</span>%</span>
                    </div>
                    <input type="range" id="redistributionRate" min="0" max="100" v-model.number="formData.redistributionRate">
                  </div>

                  <div class="field full">
                    <label>{{ t.idleTimesLabel }}</label>
                    <div class="radio-grid">
                      <label class="option-card" :class="{ selected: formData.idleTimes === 'daily' }"><input type="radio" v-model="formData.idleTimes" value="daily"> {{ t.idleDaily }}</label>
                      <label class="option-card" :class="{ selected: formData.idleTimes === 'sometimes' }"><input type="radio" v-model="formData.idleTimes" value="sometimes"> {{ t.idleSometimes }}</label>
                      <label class="option-card" :class="{ selected: formData.idleTimes === 'rarely' }"><input type="radio" v-model="formData.idleTimes" value="rarely"> {{ t.idleRarely }}</label>
                      <label class="option-card" :class="{ selected: formData.idleTimes === 'unknown' }"><input type="radio" v-model="formData.idleTimes" value="unknown"> {{ t.idleUnknown }}</label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 5 -->
              <div class="step" :class="{ active: currentStep === 5 }" v-show="currentStep === 5">
                <div class="info-box">{{ t.step5Info }}</div>
                <div class="form-grid">
                  <div class="field">
                    <label for="annualLeaveDays">{{ t.annualLeaveDaysLabel }}</label>
                    <input type="number" id="annualLeaveDays" min="0" max="365" v-model.number="formData.annualLeaveDays">
                  </div>

                  <div class="field">
                    <label for="officialHolidayDays">{{ t.officialHolidayDaysLabel }}</label>
                    <input type="number" id="officialHolidayDays" min="0" max="365" v-model.number="formData.officialHolidayDays">
                  </div>

                  <div class="field">
                    <label for="eventDays">{{ t.eventDaysLabel }}</label>
                    <input type="number" id="eventDays" min="0" max="365" v-model.number="formData.eventDays">
                  </div>

                  <div class="field">
                    <label for="emergencyDays">{{ t.emergencyDaysLabel }}</label>
                    <input type="number" id="emergencyDays" min="0" max="365" v-model.number="formData.emergencyDays">
                  </div>

                  <div class="field">
                    <label for="affectedOpportunitiesPerUnavailableDay">{{ t.affectedOppLabel }}</label>
                    <input type="number" id="affectedOpportunitiesPerUnavailableDay" min="0" v-model.number="formData.affectedOpportunitiesPerUnavailableDay">
                  </div>

                  <div class="field">
                    <label for="availabilityCommunicationLevel">{{ t.communicationLevelLabel }}</label>
                    <select id="availabilityCommunicationLevel" v-model="formData.availabilityCommunicationLevel">
                      <option value="clear">{{ t.commClear }}</option>
                      <option value="sometimes">{{ t.commSometimes }}</option>
                      <option value="no">{{ t.commNo }}</option>
                      <option value="none">{{ t.commNone }}</option>
                    </select>
                  </div>

                  <div class="field full slider-field">
                    <div class="slider-head">
                      <label for="availabilityAlternativeRate">{{ t.availabilityAlternativeRateLabel }}</label>
                      <span class="slider-value"><span>{{ formData.availabilityAlternativeRate }}</span>%</span>
                    </div>
                    <input type="range" id="availabilityAlternativeRate" min="0" max="100" v-model.number="formData.availabilityAlternativeRate">
                  </div>

                  <div class="field mobile-only">
                    <div class="summary-item">
                      <small>{{ t.totalUnavailableDays }}</small>
                      <strong>{{ calculatedResults.totalUnavailableDaysAnnual }} {{ t.days }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Navigation -->
              <div class="actions">
                <button type="button" class="btn btn-ghost" @click="prevStep" :style="{ visibility: currentStep > 1 ? 'visible' : 'hidden' }">{{ t.prev }}</button>
                <button type="button" class="btn btn-primary" @click="nextStep">
                  {{ currentStep === totalSteps ? t.showResults : t.next }}
                </button>
              </div>
            </form>
          </div>
        </div>

        <aside class="side-card">
          <h3>{{ t.liveSummary }}</h3>
          <div class="summary-list">
            <div class="summary-item">
              <small>{{ t.expectedMonthlyBookings }}</small>
              <strong>{{ formatNum(calculatedResults.monthlyBookings) }} {{ t.booking }}</strong>
            </div>
            <div class="summary-item">
              <small>{{ t.expectedRevenue }}</small>
              <strong>{{ formatMoney(calculatedResults.expectedMonthlyRevenue) }}</strong>
            </div>
            <div class="summary-item">
              <small>{{ t.monthlyLoss }}</small>
              <strong>{{ formatMoney(calculatedResults.monthlyLoss) }}</strong>
            </div>
            <div class="summary-item">
              <small>{{ t.avgUnavailableDays }}</small>
              <strong>{{ formatNum(calculatedResults.averageUnavailableDaysMonthly) }} {{ t.days }}</strong>
            </div>
          </div>
        </aside>
      </div>
    </section>

    <!-- Results Section -->
    <section class="section results" id="results" v-if="isCalculated">
      <div class="container">
        <div class="section-heading">
          <h2>{{ t.resultTitle }}</h2>
          <p>{{ t.resultDesc }}</p>
        </div>

        <div class="result-grid">
          <div class="result-panel">
            <small>{{ t.totalMonthlyLoss }}</small>
            <strong>{{ formatMoney(calculatedResults.monthlyLoss) }}</strong>
            <span class="badge danger">{{ t.potentialEst }}</span>
          </div>
          <div class="result-panel">
            <small>{{ t.confirmedLosses }}</small>
            <strong>{{ formatMoney(calculatedResults.confirmedLoss) }}</strong>
            <span class="badge warning">{{ t.noShowAndCancel }}</span>
          </div>
          <div class="result-panel">
            <small>{{ t.growthOpportunities }}</small>
            <strong>{{ formatMoney(calculatedResults.growthOpportunity) }}</strong>
            <span class="badge primary-badge">{{ t.outsideRevenue }}</span>
          </div>
          <div class="result-panel">
            <small>{{ t.recoverableRevenue }}</small>
            <strong>{{ formatMoney(calculatedResults.recoverable) }}</strong>
            <span class="badge success">{{ t.notFullyGuaranteed }}</span>
          </div>
        </div>

        <div class="assumption-grid">
          <div class="assumption-card">
            <small>{{ t.annualPotentialValue }}</small>
            <strong>{{ formatMoney(calculatedResults.annualLoss) }}</strong>
          </div>
          <div class="assumption-card">
            <small>{{ t.monthlyLossBookings }}</small>
            <strong>{{ formatNum(calculatedResults.totalLostCount) }} {{ t.booking }}</strong>
          </div>
          <div class="assumption-card">
            <small>{{ t.dailyCapacity }}</small>
            <strong>{{ formatNum(calculatedResults.dailyCapacity) }} {{ t.booking }}</strong>
          </div>
          <div class="assumption-card">
            <small>{{ t.annualAvailabilityLoss }}</small>
            <strong>{{ formatMoney(calculatedResults.monthlyAvailabilityLoss * 12) }}</strong>
          </div>
          <div class="assumption-card">
            <small>{{ t.totalUnavailableDaysAnnual }}</small>
            <strong>{{ calculatedResults.totalUnavailableDaysAnnual }} {{ t.days }}</strong>
          </div>
        </div>

        <div class="details-grid">
          <div class="breakdown-card">
            <h3>{{ t.lossBreakdownTitle }}</h3>
            <div class="desktop-table">
              <table>
                <thead>
                  <tr>
                    <th>{{ t.category }}</th>
                    <th>{{ t.source }}</th>
                    <th>{{ t.monthlyOpportunities }}</th>
                    <th>{{ t.monthlyValue }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in calculatedResults.sourcesList" :key="idx">
                    <td>{{ currentLanguage === 'en' ? item.titleEn : item.title }}</td>
                    <td>{{ currentLanguage === 'en' ? item.sourceEn : item.source }}</td>
                    <td>{{ formatNum(item.count) }}</td>
                    <td>{{ formatMoney(item.loss) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="chart-card">
            <h3>{{ t.lossComparisonTitle }}</h3>
            <div class="bar-list">
              <div class="bar-item" v-for="(item, idx) in calculatedResults.sourcesList" :key="idx">
                <div class="bar-label"><span>{{ currentLanguage === 'en' ? item.titleEn : item.title }}</span><span>{{ formatMoney(item.loss) }}</span></div>
                <div class="bar-track">
                  <div class="bar-fill" :style="{ width: ((item.loss / calculatedResults.monthlyLoss) * 100 || 0) + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="risk-card" style="margin-top:18px;">
          <h3>{{ t.riskIndicatorsTitle }}</h3>
          <div class="dual-risk-grid">
            <div class="risk-mini">
              <strong>{{ t.confirmedRiskPercent }}: {{ formatNum(calculatedResults.confirmedRiskPercent) }}%</strong>
              <div class="risk-meter"><span class="risk-pin" :style="{ left: currentLanguage === 'ar' ? 'auto' : calculatedResults.confirmedRiskPercent + '%', right: currentLanguage === 'ar' ? calculatedResults.confirmedRiskPercent + '%' : 'auto' }"></span></div>
              <p>{{ t.confirmedRiskDesc }}</p>
            </div>
            <div class="risk-mini">
              <strong>{{ t.growthRiskPercent }}: {{ formatNum(calculatedResults.growthRiskPercent) }}%</strong>
              <div class="risk-meter"><span class="risk-pin" :style="{ left: currentLanguage === 'ar' ? 'auto' : calculatedResults.growthRiskPercent + '%', right: currentLanguage === 'ar' ? calculatedResults.growthRiskPercent + '%' : 'auto' }"></span></div>
              <p>{{ t.growthRiskDesc }}</p>
            </div>
          </div>
          <p>{{ t.riskDisclaimer }}</p>
        </div>

        <div class="recommendation-card">
          <span class="badge primary-badge">{{ t.biggestSourceBadge }}</span>
          <h3>{{ t.yourMainProblem }}: {{ currentLanguage === 'en' ? calculatedResults.biggest.titleEn : calculatedResults.biggest.title }}</h3>
          <p>{{ currentLanguage === 'en' ? calculatedResults.biggest.textEn : calculatedResults.biggest.text }}</p>
          <ul class="rec-list">
            <li v-for="(rec, idx) in (currentLanguage === 'en' ? calculatedResults.biggest.recsEn : calculatedResults.biggest.recs)" :key="idx">{{ rec }}</li>
          </ul>
          <div class="report-actions">
            <button type="button" class="btn btn-secondary" @click="exportReportPdf">{{ t.exportPdf }}</button>
            <a class="btn btn-primary" href="#lead">{{ t.requestRecsBtn }}</a>
          </div>
        </div>
      </div>
    </section>

    <!-- SplendApp Lead Form -->
    <section class="section" id="splendapp">
      <div class="container">
        <div class="cta-band">
          <div class="cta-content">
            <div>
              <h2>{{ t.ctaTitle }}</h2>
              <p>{{ t.ctaDesc }}</p>
              <div class="hero-actions">
                <a class="btn btn-ghost" style="background: #fff; color: #24365c; border: none; font-weight: 800;" href="#lead">{{ t.talkToConsultant }}</a>
                <a class="btn btn-primary" style="background: rgba(58,122,254,0.85); color: #fff; border: 1px solid rgba(255,255,255,0.15);" href="#lead">{{ t.requestRecsBtn }}</a>
              </div>
            </div>
            <div class="feature-grid">
              <div class="feature-mini">{{ t.feat1 }}</div>
              <div class="feature-mini">{{ t.feat2 }}</div>
              <div class="feature-mini">{{ t.feat3 }}</div>
              <div class="feature-mini">{{ t.feat4 }}</div>
              <div class="feature-mini">{{ t.feat5 }}</div>
              <div class="feature-mini">{{ t.feat6 }}</div>
            </div>
          </div>
        </div>

        <div class="lead-card" id="lead">
          <h2>{{ t.leadTitle }}</h2>
          <p>{{ t.leadDesc }}</p>
          <form class="form-grid" @submit.prevent="handleLeadSubmit">
            <div class="field">
              <label for="leadName">{{ t.name }}</label>
              <input id="leadName" type="text" v-model="leadData.name" :placeholder="t.nameHolder" required>
            </div>
            <div class="field">
              <label for="businessName">{{ t.businessName }}</label>
              <input id="businessName" type="text" v-model="leadData.businessName" :placeholder="t.businessNameHolder" required>
            </div>
            <div class="field">
              <label for="leadPhone">{{ t.whatsapp }}</label>
              <input id="leadPhone" type="tel" v-model="leadData.phone" placeholder="01xxxxxxxxx" required>
            </div>
            <div class="field">
              <label for="leadEmail">{{ t.email }}</label>
              <input id="leadEmail" type="email" v-model="leadData.email" placeholder="name@example.com" required>
            </div>
            <div class="field">
              <label for="country">{{ t.country }}</label>
              <select id="country" v-model="leadData.country">
                <option value="Egypt">{{ currentLanguage === 'en' ? 'Egypt' : 'مصر' }}</option>
                <option value="KSA">{{ currentLanguage === 'en' ? 'Saudi Arabia' : 'السعودية' }}</option>
                <option value="UAE">{{ currentLanguage === 'en' ? 'UAE' : 'الإمارات' }}</option>
                <option value="Kuwait">{{ currentLanguage === 'en' ? 'Kuwait' : 'الكويت' }}</option>
                <option value="Qatar">{{ currentLanguage === 'en' ? 'Qatar' : 'قطر' }}</option>
                <option value="Oman">{{ currentLanguage === 'en' ? 'Oman' : 'عمان' }}</option>
                <option value="Bahrain">{{ currentLanguage === 'en' ? 'Bahrain' : 'البحرين' }}</option>
                <option value="Other">{{ currentLanguage === 'en' ? 'Other' : 'أخرى' }}</option>
              </select>
            </div>
            <div class="field">
              <label for="timeline">{{ t.timeline }}</label>
              <select id="timeline" v-model="leadData.timeline">
                <option value="now">{{ currentLanguage === 'en' ? 'Immediately' : 'فوراً' }}</option>
                <option value="month">{{ currentLanguage === 'en' ? 'Within a month' : 'خلال شهر' }}</option>
                <option value="later">{{ currentLanguage === 'en' ? 'Later' : 'لاحقاً' }}</option>
              </select>
            </div>
            <div class="field full">
              <button type="submit" class="btn btn-secondary btn-full">{{ t.submitLead }}</button>
              <button type="button" class="btn btn-ghost btn-full" @click="downloadRecommendationsPdf" style="margin-top:10px">{{ t.downloadGeneralPdf }}</button>
              <div class="success-message" v-if="leadSuccessMessage">{{ leadSuccessMessage }}</div>
            </div>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import enTranslations from './booking-loss-en';
import arTranslations from './booking-loss-ar';
import { notifyToolSuccess } from '@/utils/promoPopup';

export default {
  name: 'BookingLossCalculatorCom',
  data() {
    return {
      isCalculated: false,
      currentLanguage: this.$store.state.currentLanguage,
      emailConfig: {
        mode: "live",
        webhookUrl: "https://script.google.com/macros/s/AKfycbz4jJeElO3yiuIP0naP1mvx1CQtn8tL7SrheTDP--XU7nA9PpBWVIoPJNPEnxvyYWrX/exec",
        fromName: "SplendApp Tools",
        subject: "توصيات عملية لتقليل الفرص المالية غير المستغلة في الحجوزات",
        pdfFileName: "splendapp-booking-recommendations.pdf",
        attachPdfBase64: true,
      },
      pdfBase64String: "JVBERi0xLjcKJcK1wrYKCjEgMCBvYmoKPDwvUHJvZHVjZXIocHlwZGYpPj4KZW5kb2JqCgoy...",
      currentStep: 1,
      totalSteps: 5,
      formData: {
        serviceCategory: "medical",
        serviceType: "",
        currency: "ج.م",
        deliveryMode: ["center"],
        complexity: "none",
        dailyBookings: 20,
        avgValue: 500,
        workingDays: 26,
        avgDuration: 45,
        resourceCount: 2,
        workingHours: 8,
        noShowRate: 15,
        cancelRate: 10,
        compensatedCancelRate: 30,
        missedCalls: 5,
        callConversionRate: 40,
        peakLostBookings: 4,
        redistributionRate: 50,
        idleTimes: "daily",
        annualLeaveDays: 14,
        officialHolidayDays: 10,
        eventDays: 6,
        emergencyDays: 5,
        affectedOpportunitiesPerUnavailableDay: 4,
        availabilityCommunicationLevel: "sometimes",
        availabilityAlternativeRate: 60
      },
      leadData: {
        name: "",
        businessName: "",
        phone: "",
        email: "",
        country: "Egypt",
        timeline: "now"
      },
      leadSuccessMessage: "",
      industryOptions: {
        medical: [
          { value: "clinic", label: "عيادات تخصصية", labelEn: "Specialized Clinics" },
          { value: "dental", label: "مراكز أسنان", labelEn: "Dental Centers" },
          { value: "derma", label: "جلدية وتجميل", labelEn: "Derma & Beauty" },
          { value: "physio", label: "علاج طبيعي وتأهيل", labelEn: "Physiotherapy & Rehab" },
          { value: "lab_rad", label: "مختبرات ومراكز أشعة", labelEn: "Labs & Radiology" },
          { value: "other", label: "نشاط طبي/صحي آخر", labelEn: "Other Medical/Health" }
        ],
        education: [
          { value: "academy", label: "مراكز تعليمية وأكاديميات", labelEn: "Educational Centers & Academies" },
          { value: "training", label: "مراكز تدريب وكورسات", labelEn: "Training Centers & Courses" },
          { value: "instructors", label: "مدربون ومدرسون مستقلون", labelEn: "Independent Tutors & Instructors" },
          { value: "other", label: "نشاط تعليمي آخر", labelEn: "Other Educational Activity" }
        ],
        professional: [
          { value: "legal", label: "مكاتب محاماة واستشارات قانونية", labelEn: "Law Firms & Legal Consultancy" },
          { value: "accounting", label: "مكاتب محاسبة ومراجعة", labelEn: "Accounting & Auditing Firms" },
          { value: "engineering", label: "مكاتب استشارات هندسية", labelEn: "Engineering Consultancy" },
          { value: "consulting", label: "استشارات أعمال وإدارة", labelEn: "Business & Management Consulting" },
          { value: "other", label: "خدمات مهنية أخرى", labelEn: "Other Professional Services" }
        ],
        field: [
          { value: "maintenance", label: "صيانة منزلية وأجهزة", labelEn: "Home & Appliance Maintenance" },
          { value: "cleaning", label: "خدمات تنظيف ومكافحة حشرات", labelEn: "Cleaning & Pest Control" },
          { value: "automotive", label: "مراكز صيانة سيارات", labelEn: "Auto Maintenance Centers" },
          { value: "other", label: "خدمات ميدانية أخرى", labelEn: "Other Field Services" }
        ],
        rental: [
          { value: "studios", label: "استوديوهات تصوير ومساحات إبداعية", labelEn: "Photography Studios & Creative Spaces" },
          { value: "spaces", label: "مساحات عمل مشتركة وقاعات", labelEn: "Co-working Spaces & Halls" },
          { value: "sports", label: "ملاعب صالات ومرافق رياضية", labelEn: "Sports Courts & Facilities" },
          { value: "other", label: "تأجير ومساحات أخرى", labelEn: "Other Rental Spaces" }
        ],
        lifestyle: [
          { value: "salon_spa", label: "صالونات تجميل ومراكز سبا", labelEn: "Beauty Salons & Spas" },
          { value: "fitness", label: "صالات لياقة بدنية وتدريب شخصي", labelEn: "Fitness Centers & Personal Training" },
          { value: "catering", label: "مناسبات وضيافة وحجوزات مطاعم", labelEn: "Events, Catering & Restaurant Bookings" },
          { value: "other", label: "أنشطة لايف ستايل وضيافة أخرى", labelEn: "Other Lifestyle & Hospitality" }
        ]
      }
    };
  },
  computed: {
    t() {
      return this.currentLanguage === 'en' ? enTranslations : arTranslations;
    },
    stepTitles() {
      return {
        1: { title: this.t.step1Title, desc: this.t.step1Desc },
        2: { title: this.t.step2Title, desc: this.t.step2Desc },
        3: { title: this.t.step3Title, desc: this.t.step3Desc },
        4: { title: this.t.step4Title, desc: this.t.step4Desc },
        5: { title: this.t.step5Title, desc: this.t.step5Desc }
      };
    },
    currentStepData() {
        return this.stepTitles[this.currentStep];
    },
    currentServiceOptions() {
      return this.industryOptions[this.formData.serviceCategory] || [];
    },
    progressPercent() {
      return (this.currentStep / this.totalSteps) * 100;
    },
    calculatedResults() {
      const input = this.formData;
      const monthlyBookings = input.dailyBookings * input.workingDays;
      const expectedMonthlyRevenue = monthlyBookings * input.avgValue;

      const noShowCount = monthlyBookings * (input.noShowRate / 100);
      const noShowLoss = noShowCount * input.avgValue;

      const totalCancelCount = monthlyBookings * (input.cancelRate / 100);
      const uncompensatedCancelCount = totalCancelCount * (1 - (input.compensatedCancelRate / 100));
      const cancelLoss = uncompensatedCancelCount * input.avgValue;

      const missedCallsMonthly = input.missedCalls * input.workingDays;
      const lostBookingFromCallsCount = missedCallsMonthly * (input.callConversionRate / 100);
      const missedCallsLoss = lostBookingFromCallsCount * input.avgValue;

      const monthlyPeakLostBookings = input.peakLostBookings * input.workingDays;
      const unrecoverablePeakBookingsCount = monthlyPeakLostBookings * (1 - (input.redistributionRate / 100));
      const peakLoss = unrecoverablePeakBookingsCount * input.avgValue;
      const recoverablePeakRevenue = (monthlyPeakLostBookings * (input.redistributionRate / 100)) * input.avgValue;

      const totalUnavailableDaysAnnual = Number(input.annualLeaveDays) + 
                                         Number(input.officialHolidayDays) + 
                                         Number(input.eventDays) + 
                                         Number(input.emergencyDays);
      const averageUnavailableDaysMonthly = totalUnavailableDaysAnnual / 12;

      let communicationFactor = 1.0;
      if (input.availabilityCommunicationLevel === "clear") communicationFactor = 0.2;
      else if (input.availabilityCommunicationLevel === "sometimes") communicationFactor = 0.6;
      else if (input.availabilityCommunicationLevel === "no") communicationFactor = 0.9;
      else if (input.availabilityCommunicationLevel === "none") communicationFactor = 1.0;

      const annualAffectedOpportunities = totalUnavailableDaysAnnual * input.affectedOpportunitiesPerUnavailableDay * communicationFactor;
      const annualAvailabilityLoss = annualAffectedOpportunities * input.avgValue;
      const monthlyAvailabilityLoss = annualAvailabilityLoss / 12;
      const monthlyAffectedOpportunities = annualAffectedOpportunities / 12;

      const monthlyRecoverableAvailabilityRevenue = (monthlyAffectedOpportunities * (input.availabilityAlternativeRate / 100)) * input.avgValue;

      const totalMonthlyLoss = noShowLoss + cancelLoss + missedCallsLoss + peakLoss + monthlyAvailabilityLoss;
      const totalAnnualLoss = totalMonthlyLoss * 12;

      const confirmedLoss = noShowLoss + cancelLoss;
      const growthOpportunity = missedCallsLoss + peakLoss + monthlyAvailabilityLoss;
      const totalRecoverable = recoverablePeakRevenue + monthlyRecoverableAvailabilityRevenue;

      const confirmedRiskPercent = expectedMonthlyRevenue > 0 ? (confirmedLoss / expectedMonthlyRevenue) * 100 : 0;
      const growthRiskPercent = expectedMonthlyRevenue > 0 ? (growthOpportunity / expectedMonthlyRevenue) * 100 : 0;

      const dailyCapacity = Math.round((input.workingHours * 60 / input.avgDuration) * input.resourceCount);

      let biggestSource = { key: "none", title: "لا توجد خسائر واضحة", titleEn: "No clear losses", loss: 0 };
      const sources = [
        { key: "noShow", title: "عدم حضور العملاء", titleEn: "Customer No-Shows", loss: noShowLoss, text: "تتأثر أرباحك بشكل مباشر من حجز فترات زمنية مؤكدة يتبعها عدم حضور العميل دون إعلام مسبق.", textEn: "Your profits are directly affected by confirmed bookings followed by no-shows without prior notice.", recs: ["تفعيل رسائل تذكير تلقائية ومجدولة عبر الواتساب قبل الموعد بـ 24 ساعة.", "تطبيق سياسة الدفع المسبق أو العربون للحجوزات لأول مرة أو فترات الذروة."], recsEn: ["Activate automated WhatsApp reminders 24 hours before the appointment.", "Apply an advance payment or deposit policy for first-time bookings or peak periods."] },
        { key: "cancellations", title: "إلغاء المواعيد دون تعويض", titleEn: "Uncompensated Cancellations", loss: cancelLoss, text: "تخسر حجوزات مؤكدة يتم إلغاؤها دون وجود آلية مرنة لإعادة الجدولة السريعة لعملاء آخرين.", textEn: "You lose confirmed bookings that are canceled without a flexible mechanism for quick rescheduling for other customers.", recs: ["بناء روابط مخصصة لإلغاء أو إعادة جدولة الخدمة ذاتياً من طرف العميل لتوفير الوقت.", "إتاحة تفعيل قائمة انتظار (Waitlist) ذكية لتسكين عملاء بدلاء فور حدوث إلغاء."], recsEn: ["Build custom links for self-cancellation or rescheduling by the customer to save time.", "Enable a smart waitlist to immediately fill spots upon cancellation."] },
        { key: "missedCalls", title: "المكالمات والفرص الفائتة", titleEn: "Missed Calls & Opportunities", loss: missedCallsLoss, text: "عدم توفر رد فوري وقنوات حجز مؤتمتة على مدار الساعة يضيع فرص نمو واكتساب عملاء جدد.", textEn: "Lack of immediate response and automated booking channels 24/7 wastes growth opportunities and acquiring new customers.", recs: ["الاعتماد على نظام حجز أونلاين ذكي يعمل 24/7 لاستقبال الطلبات دون تدخل بشري.", "توفير الشات بوت الرد الآلي الموحد (Interactive Bot) لمعالجة الطلبات عبر الواتساب."], recsEn: ["Rely on a smart 24/7 online booking system to receive requests without human intervention.", "Provide a unified interactive chatbot to process requests via WhatsApp."] },
        { key: "distribution", title: "سوء توزيع الحجوزات والازدحام", titleEn: "Poor Booking Distribution", loss: peakLoss, text: "تواجه طلباً كثيفاً في فترات الذروة مما يضطر العملاء للمغادرة، يقابله هدر تشغيلي في فترات الركود.", textEn: "You face heavy demand in peak periods causing customers to leave, offset by operational waste in quiet periods.", recs: ["تقديم عروض تشجيعية وأسعار تفضيلية مخصصة للفترات الهادئة أو الصباحية لتوزيع الأحمال.", "تنظيم الطاقة الاستيعابية عبر لوحة تحكم موحدة ومترابطة لكافة الفروع ومقدمي الخدمة."], recsEn: ["Offer promotional deals and preferential prices for quiet or morning periods to distribute the load.", "Organize capacity via a unified and interconnected dashboard for all branches and service providers."] },
        { key: "availability", title: "أيام الانقطاع وضعف التواصل حول التوافر", titleEn: "Downtime & Poor Availability Communication", loss: monthlyAvailabilityLoss, text: "يتعرض عملك لتوقفات إجازات دون إخطار استباقي وواضح للعملاء، مما يفقدك طلبات كامنة.", textEn: "Your business experiences leave stops without clear and proactive notification to customers, losing potential requests.", recs: ["ربط فترات الإجازات بجدول الحجز الإلكتروني فوراً لغلق الأيام غير المتاحة أمام العميل تلقائياً.", "إرسال حملات إعلامية مسبقة عبر البريد أو رسائل البوش تخطر العملاء بالتوافر الاستثنائي."], recsEn: ["Immediately link leave periods to the electronic booking schedule to automatically close unavailable days.", "Send proactive media campaigns via email or push notifications to notify customers of exceptional availability."] }
      ];

      sources.forEach(src => {
        if (src.loss > biggestSource.loss) {
          biggestSource = src;
        }
      });

      let leadScore = 30;
      if (totalMonthlyLoss > 50000) leadScore += 30;
      else if (totalMonthlyLoss > 15000) leadScore += 15;
      if (input.complexity !== "none") leadScore += 20;
      if (confirmedRiskPercent > 15) leadScore += 20;

      const totalLostCount = noShowCount + uncompensatedCancelCount + lostBookingFromCallsCount + unrecoverablePeakBookingsCount + monthlyAffectedOpportunities;

      return {
        monthlyBookings,
        expectedMonthlyRevenue,
        noShowLoss,
        cancelLoss,
        missedCallsLoss,
        peakLoss,
        monthlyAvailabilityLoss,
        totalUnavailableDaysAnnual,
        averageUnavailableDaysMonthly,
        monthlyLoss: totalMonthlyLoss,
        annualLoss: totalAnnualLoss,
        confirmedLoss,
        growthOpportunity,
        recoverable: totalRecoverable,
        confirmedRiskPercent,
        growthRiskPercent,
        dailyCapacity,
        leadScore,
        biggest: biggestSource,
        totalLostCount,
        sourcesList: [
          { title: "عدم حضور العملاء", titleEn: "Customer No-Shows", source: "خسائر حجوزات مؤكدة", sourceEn: "Confirmed Booking Losses", count: Math.round(noShowCount), loss: noShowLoss },
          { title: "إلغاء غير معوض", titleEn: "Uncompensated Cancellation", source: "خسائر حجوزات مؤكدة", sourceEn: "Confirmed Booking Losses", count: Math.round(uncompensatedCancelCount), loss: cancelLoss },
          { title: "فرص ضائعة من المكالمات", titleEn: "Missed Call Opportunities", source: "فرص نمو فائتة", sourceEn: "Missed Growth Opportunities", count: Math.round(lostBookingFromCallsCount), loss: missedCallsLoss },
          { title: "سوء توزيع المواعيد", titleEn: "Poor Appointment Distribution", source: "فرص نمو فائتة", sourceEn: "Missed Growth Opportunities", count: Math.round(unrecoverablePeakBookingsCount), loss: peakLoss },
          { title: "انقطاع الخدمة وضعف التواصل", titleEn: "Service Downtime & Poor Comm", source: "أثر فترات التوقف", sourceEn: "Impact of Downtimes", count: Math.round(monthlyAffectedOpportunities), loss: monthlyAvailabilityLoss }
        ]
      };
    }
  },
  watch: {
    '$store.state.currentLanguage'(newLang) {
      this.currentLanguage = newLang;
    },
    'formData.serviceCategory'(newCategory) {
      const options = this.industryOptions[newCategory];
      if (options && options.length > 0) {
        this.formData.serviceType = options[0].value;
      }
    }
  },
  mounted() {
    if (this.currentServiceOptions.length > 0) {
      this.formData.serviceType = this.currentServiceOptions[0].value;
    }
  },
  methods: {
    nextStep() {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      } else {
        this.isCalculated = true;
        notifyToolSuccess('booking-loss-calculator');
        this.$nextTick(() => {
          this.scrollToElement("results");
        });
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    scrollToElement(id) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    },
    formatMoney(value) {
      if (this.currentLanguage === 'en') {
        const c = this.formData.currency;
        const curCode = c === 'ج.م' ? 'EGP' : c === 'ر.س' ? 'SAR' : c === 'د.إ' ? 'AED' : c === '$' ? 'USD' : c === 'د.ك' ? 'KWD' : c === 'ر.ق' ? 'QAR' : c === 'ر.ع' ? 'OMR' : 'BHD';
        return Math.round(value).toLocaleString('en-US') + ' ' + curCode;
      }
      return Math.round(value).toLocaleString('ar-EG') + ' ' + this.formData.currency;
    },
    formatNum(value) {
      return this.currentLanguage === 'en' ? Math.round(value).toLocaleString('en-US') : Math.round(value).toLocaleString('ar-EG');
    },
    async handleLeadSubmit() {
      const result = this.calculatedResults;
      const payload = {
        to_email: this.leadData.email,
        subject: this.emailConfig.subject,
        lead: {
          name: this.leadData.name,
          business_name: this.leadData.businessName,
          phone: this.leadData.phone,
          email: this.leadData.email,
          country: this.leadData.country,
          timeline: this.leadData.timeline
        },
        calculation: {
          currency: this.formData.currency,
          industry_category: this.formData.serviceCategory,
          industry_sub_type: this.formData.serviceType,
          expected_monthly_bookings: Math.round(result.monthlyBookings),
          expected_monthly_revenue: Math.round(result.expectedMonthlyRevenue),
          no_show_monthly_loss: Math.round(result.noShowLoss),
          cancel_monthly_loss: Math.round(result.cancelLoss),
          missed_calls_monthly_loss: Math.round(result.missedCallsLoss),
          peak_distribution_monthly_loss: Math.round(result.peakLoss),
          availability_monthly_loss: Math.round(result.monthlyAvailabilityLoss),
          availability_communication_level: this.formData.availabilityCommunicationLevel,
          monthly_unutilized_opportunity: Math.round(result.monthlyLoss),
          monthly_loss: Math.round(result.monthlyLoss),
          annual_loss: Math.round(result.annualLoss),
          recoverable_monthly_revenue: Math.round(result.recoverable),
          biggest_loss_source: result.biggest.title,
          lead_score: result.leadScore
        }
      };

      if (this.emailConfig.mode === "demo") {
        this.leadSuccessMessage = this.t.leadSuccessDemo + result.leadScore + "/100";
        return;
      }

      try {
        await fetch(this.emailConfig.webhookUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(payload)
        });

        // With no-cors, the response is opaque (response.ok is always false)
        // We assume success if fetch didn't throw a network error.
        this.leadSuccessMessage = this.t.leadSuccessReal;
      } catch (error) {
        this.leadSuccessMessage = this.t.leadError + "Network/CORS Error";
      }
    },
    exportReportPdf() {
      window.print();
    },
    downloadRecommendationsPdf() {
      const linkSource = `data:application/pdf;base64,${this.pdfBase64String}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = this.emailConfig.pdfFileName;
      downloadLink.click();
    }
  }
};
</script>

<style scoped>
    * { box-sizing: border-box; }
    
    .calculator-container {
      font-family: "Cairo", "DM Sans", sans-serif;
      background-color: #F6F9FB;
    }

    .btn {
      border: 0; cursor: pointer; border-radius: 200px; padding: 13px 22px;
      font-weight: 800; font-size: 0.98rem; display: inline-flex; align-items: center;
      justify-content: center; gap: 8px; transition: 0.2s ease; min-height: 48px;
    }
    .btn:hover { transform: translateY(-1px); }
    .btn-primary { background: #3a7afe; color: #fff; box-shadow: 0 10px 20px rgba(58,122,254,0.2); }
    .btn-secondary { background: #24365c; color: #fff; }
    .btn-ghost { background: #fff; color: #24365c; border: 1px solid #e2e8f0; }
    .btn-primary-light { background: #fff; color: #3a7afe; box-shadow: 0 10px 20px rgba(0,0,0,0.1); }
    .btn-full { width: 100%; }

    .section { padding: 36px 0; }
    .container { width: min(1180px, calc(100% - 32px)); margin: 0 auto; }

    .tool-shell { display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 24px; align-items: start; }
    
    .calculator-card, .side-card, .result-panel, .recommendation-card, .lead-card, .breakdown-card, .chart-card, .risk-card, .assumption-card {
      background: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px; box-shadow: 0 4px 15px rgba(0,0,0,0.02);
    }
    .calculator-card { overflow: hidden; }

    .step-header { padding: 24px 26px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; gap: 16px; }
    .step-title h2 { margin: 0 0 4px; font-size: clamp(1.35rem, 3vw, 1.9rem); color: #24365c; }
    .step-title p { margin: 0; color: #716c80; font-weight: 500; }

    .progress-wrap { min-width: 150px; text-align: left; }
    [dir="rtl"] .progress-wrap { text-align: right; }
    .progress-label { font-weight: 800; color: #3a7afe; font-size: 0.9rem; margin-bottom: 8px; }
    .progress-bar { height: 9px; background: #edf3ff; border-radius: 200px; overflow: hidden; }
    .progress-fill { height: 100%; background: #3a7afe; border-radius: 200px; transition: width 0.22s ease; }

    .step-body { padding: 26px; }
    .form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
    .field { display: grid; gap: 8px; }
    .field.full { grid-column: 1 / -1; }

    label { font-weight: 700; color: #24365c; font-size: 0.95rem; }
    .helper { color: #716c80; font-size: 0.85rem; margin-top: -2px; line-height: 1.5; }

    input, select {
      width: 100%; min-height: 52px; border: 1px solid #e2e8f0; background: #fff;
      border-radius: 6px; padding: 12px 14px; font-size: 1rem; color: #24365c;
      outline: none; transition: border 0.16s ease;
    }
    input:focus, select:focus { border-color: #3a7afe; }

    .input-unit { position: relative; }
    [dir="rtl"] .input-unit input { padding-left: 14px; padding-right: 72px; }
    [dir="ltr"] .input-unit input { padding-right: 14px; padding-left: 72px; }
    .unit {
      position: absolute; top: 50%; transform: translateY(-50%);
      color: #716c80; font-weight: 700; background: #f8fafc;
      border: 1px solid #e2e8f0; border-radius: 200px; padding: 4px 9px;
      font-size: 0.84rem; pointer-events: none;
    }
    [dir="rtl"] .unit { right: 12px; left: auto; }
    [dir="ltr"] .unit { left: 12px; right: auto; }

    .radio-grid, .industry-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .option-card {
      border: 1px solid #e2e8f0; background: #fff; border-radius: 6px; padding: 12px 13px;
      font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px;
      min-height: 48px; transition: 0.16s ease; user-select: none; color: #24365c;
    }
    .option-card:hover { border-color: #3a7afe; }
    .option-card input { width: auto; min-height: auto; accent-color: #3a7afe; }
    .option-card.selected { border-color: #3a7afe; background: #F6F9FB; color: #3a7afe; }

    .slider-field { background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 16px; }
    .slider-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 10px; }
    .slider-value { font-weight: 800; color: #3a7afe; background: #F6F9FB; padding: 5px 10px; border-radius: 200px; min-width: 58px; text-align: center; }
    input[type="range"] { padding: 0; min-height: 26px; accent-color: #3a7afe; border: 0; box-shadow: none; }

    .actions { display: flex; justify-content: space-between; gap: 12px; padding-top: 24px; margin-top: 8px; border-top: 1px solid #e2e8f0; }

    .side-card { padding: 20px; position: sticky; top: 94px; }
    .side-card h3 { margin: 0 0 14px; font-size: 1.18rem; color: #24365c; }
    .summary-list { display: grid; gap: 10px; }
    .summary-item { padding: 14px; border-radius: 6px; background: #F6F9FB; border: 1px solid #e2e8f0; }
    .summary-item small { display: block; color: #716c80; font-weight: 700; margin-bottom: 4px; }
    .summary-item strong { display: block; font-size: 1.3rem; color: #24365c; letter-spacing: -0.03em; }

    .info-box { padding: 16px; border-radius: 6px; background: #F6F9FB; border: 1px solid #e2e8f0; margin-bottom: 18px; color: #716c80; font-weight: 600; }

    .results { display: block; padding-top: 20px; }
    .section-heading { text-align: center; margin-bottom: 24px; }
    .section-heading h2 { font-size: clamp(1.8rem, 4vw, 2.8rem); margin: 0 0 10px; color: #24365c; }
    .section-heading p { color: #716c80; max-width: 720px; margin: 0 auto; font-size: 1.06rem; }

    .result-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; margin-bottom: 18px; }
    .result-panel { padding: 20px; position: relative; overflow: hidden; }
    .result-panel small { color: #716c80; font-weight: 800; display: block; margin-bottom: 7px; position: relative; z-index: 1; }
    .result-panel strong { display: block; font-size: clamp(1.42rem, 3vw, 2.05rem); color: #24365c; line-height: 1.1; position: relative; z-index: 1; }

    .badge { display: inline-flex; align-items: center; gap: 6px; margin-top: 12px; border-radius: 200px; padding: 6px 10px; font-weight: 800; font-size: 0.86rem; position: relative; z-index: 1; }
    .badge.danger { background: #fee2e2; color: #dc2626; }
    .badge.warning { background: #fef3c7; color: #b45309; }
    .badge.success { background: #dcfce7; color: #16a34a; }
    .badge.primary-badge { background: #eef0ff; color: #3a7afe; }

    .details-grid { display: grid; grid-template-columns: 1fr 0.86fr; gap: 18px; align-items: start; }
    .breakdown-card, .chart-card, .risk-card { padding: 22px; }
    .breakdown-card h3, .chart-card h3, .risk-card h3 { margin: 0 0 16px; font-size: 1.35rem; color: #24365c; }

    table { width: 100%; border-collapse: collapse; border-radius: 6px; font-size: 0.96rem; }
    th, td { padding: 14px 12px; border-bottom: 1px solid #e2e8f0; text-align: start; }
    th { color: #716c80; background: #F6F9FB; font-weight: 800; }
    tr:last-child td { border-bottom: 0; }

    .bar-list { display: grid; gap: 14px; }
    .bar-item { display: grid; gap: 7px; }
    .bar-label { display: flex; justify-content: space-between; gap: 10px; font-weight: 800; color: #24365c; }
    .bar-track { height: 13px; background: #f1f5f9; border-radius: 200px; overflow: hidden; }
    .bar-fill { height: 100%; background: #3a7afe; border-radius: 200px; transition: width 0.6s ease; }

    .assumption-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-top: 18px; }
    .assumption-card { background: #F6F9FB; padding: 14px; }
    .assumption-card small { display: block; color: #716c80; font-weight: 800; margin-bottom: 4px; }
    .assumption-card strong { display: block; font-size: 1.1rem; color: #3a7afe; }

    .dual-risk-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; margin-top: 12px; }
    .risk-mini { background: #F6F9FB; padding: 16px; }
    .risk-mini strong { display: block; margin-bottom: 10px; font-size: 1.05rem; color: #24365c;}
    .risk-meter { height: 16px; background: linear-gradient(to right, #16a34a, #f59e0b, #dc2626); border-radius: 200px; position: relative; margin: 16px 0 12px; }
    .risk-pin { position: absolute; top: 50%; width: 24px; height: 24px; border-radius: 50%; background: #fff; border: 5px solid #24365c; transform: translateY(-50%); box-shadow: 0 6px 14px rgba(0,0,0, 0.1); }
    .risk-card p { color: #716c80; margin: 8px 0 0; }

    .recommendation-card { padding: 24px; margin-top: 18px; }
    .recommendation-card h3 { margin: 0 0 8px; font-size: 1.5rem; color: #24365c; }
    .recommendation-card p { color: #716c80; margin: 0 0 16px; }

    .rec-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; padding: 0; margin: 0; list-style: none; }
    .rec-list li { background: #F6F9FB; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px 14px; font-weight: 700; color: #24365c; }

    .report-actions { display: flex; gap: 10px; margin-top: 20px; }

    .cta-band { background: linear-gradient(to left, #24365c, #3a7afe); color: #fff; border-radius: 7px; padding: clamp(26px, 5vw, 46px); overflow: hidden; position: relative; margin-top: 8px; }
    .cta-content { position: relative; z-index: 1; display: grid; grid-template-columns: 1fr 0.9fr; gap: 28px; align-items: center; }
    .cta-band h2 { font-size: clamp(1.8rem, 4vw, 3rem); margin: 0 0 12px; }
    .cta-band p { margin: 0 0 22px; opacity: 0.9; font-size: 1.08rem; }

    .feature-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .feature-mini { background: rgba(255,255,255,0.15); border-radius: 6px; padding: 13px; font-weight: 800; font-size: 0.95rem; }

    .lead-card { padding: 36px; margin-top: 24px; }
    .lead-card h2 { margin: 0 0 10px; font-size: clamp(1.5rem, 3vw, 2.1rem); color: #24365c; }
    .lead-card p { color: #716c80; margin: 0 0 24px; max-width: 600px; }
    .success-message { margin-top: 15px; padding: 15px; background: #eef0ff; color: #3a7afe; border-radius: 6px; font-weight: 700; text-align: center; }

    @media (max-width: 950px) {
      .tool-shell { grid-template-columns: 1fr; }
      .side-card { position: static; }
      .result-grid { grid-template-columns: repeat(2, 1fr); }
      .details-grid { grid-template-columns: 1fr; }
      .cta-content { grid-template-columns: 1fr; }
    }
    @media (max-width: 600px) {
      .container { width: min(100% - 20px, 1180px); }
      .section { padding: 20px 0; }
      .step-header {
        flex-direction: column;
        align-items: stretch;
        gap: 14px;
        padding: 18px 16px;
      }
      .step-title h2 {
        font-size: 1.28rem;
        line-height: 1.3;
      }
      .step-title p {
        font-size: 0.92rem;
        line-height: 1.55;
      }
      .progress-wrap {
        width: 100%;
        min-width: 0;
      }
      .progress-label {
        display: flex;
        justify-content: space-between;
        margin-bottom: 7px;
      }
      .step-body { padding: 18px 16px; }
      .form-grid { grid-template-columns: 1fr; }
      .radio-grid, .industry-grid { grid-template-columns: 1fr; }
      .result-grid { grid-template-columns: 1fr; }
      .assumption-grid { grid-template-columns: 1fr; }
      .dual-risk-grid { grid-template-columns: 1fr; }
      .rec-list { grid-template-columns: 1fr; }
    }
</style>
